import { normalizeUrlProtocol } from 'containers/VerticalPage/common/utils'
import { saveAs } from 'file-saver'
import { useCallback, useContext, useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { VerticalContext } from 'store/verticals/Context'
import { IRegistry } from 'store/verticals/types'
import { generateCompleteFileName } from 'utils/fileNameGenerator'
import { formattedDate } from 'utils/formatters'
import { columns } from './registryMatches/columns'

const allBusinessNameTableHeader = ['Entity Official Names', 'Source']

const addAddressesHeader = ['Address', 'Source']

const useRows = () => {
  const { verticalEntity } = useContext(VerticalContext)
  return useMemo(() => {
    return (
      verticalEntity?.overviewDetails.registry?.map((item: IRegistry) => {
        const businessNamesArray = item.names.filter(el => el.key !== 'dba_name')
        const dbaObject = item.names.find(el => el.key === 'dba_name')

        return {
          id: item.ids.map((idObj, index) => `${idObj.key}--${idObj.value}--${index}`).join('-'),
          regId: item.ids,
          businessNames: businessNamesArray.map(el => el.value),
          dba: dbaObject?.value || 'N/A',
          addresses: item?.addresses?.map(addressObj => addressObj.value),
          status: (() => {
            if (!item?.endDate) return 'Active'
            const endDate = new Date(item.endDate)
            const now = new Date()
            return endDate > now ? 'Active' : 'Inactive'
          })(),
          startDate: item?.startDate ? formattedDate(item.startDate, 'MMMM DD, YYYY') : 'N/A',
          endDate: item?.endDate ? formattedDate(item.endDate, 'MMMM DD, YYYY') : 'N/A',
        }
      }) || []
    )
  }, [verticalEntity?.overviewDetails.registry])
}

export const useCSVExportCallback = () => {
  const { verticalEntity } = useContext(VerticalContext)
  const match = useRouteMatch<{ vertical: string; caseId: string }>('/vertical/:vertical/:caseId')
  const vertical = match?.params.vertical || ''
  const rows = useRows()
  return useCallback(() => {
    const csvSections = []

    if (rows.length > 0) {
      csvSections.push('=== REGISTRIES FOUND ===')
      const registriesHeaders = columns.map(col => col.title).join(',')
      const registriesData = rows.map(row =>
        columns
          .map(col => {
            const value = row[col.key as keyof typeof row]

            if (col.key === 'regId') {
              return `"${(value as Array<any>).map(({ key, value }: any) => `${key}: ${value}`).join(' | ')}"`
            }

            if (Array.isArray(value)) {
              return `"${value.join('; ')}"`
            }

            return `"${value ?? 'N/A'}"`
          })
          .join(','),
      )
      csvSections.push(registriesHeaders)
      csvSections.push(...registriesData)
    }

    // Export ALL BUSINESS Names / DBA table
    const businessNamesData = (verticalEntity?.overviewDetails?.businessNames || []).map(source => [
      `"${source.name}"`,
      `"${source.sources.map(dataSource => `${dataSource.platform}: ${dataSource.uri}`).join('; ')}"`,
    ])

    if (businessNamesData.length > 0) {
      csvSections.push('') // Empty row for separation
      csvSections.push('=== ALL BUSINESS NAMES / DBA ===')
      const businessNamesHeaders = allBusinessNameTableHeader.join(',')
      csvSections.push(businessNamesHeaders)
      csvSections.push(...businessNamesData.map(row => row.join(',')))
    }

    // Export All Addresses table
    const addressesData = (verticalEntity?.overviewDetails.locations || []).flatMap(location =>
      location.businesses.map(business => [
        `"${business.address}"`,
        `"${business.sources.map(dataSource => `${dataSource.platform}: ${normalizeUrlProtocol(dataSource?.uri)}`).join('; ')}"`,
      ]),
    )

    if (addressesData.length > 0) {
      csvSections.push('') // Empty row for separation
      csvSections.push('=== ALL ADDRESSES ===')
      const addressesHeaders = addAddressesHeader.join(',')
      csvSections.push(addressesHeaders)
      csvSections.push(...addressesData.map(row => row.join(',')))
    }

    // Create single CSV file
    const csvContent = csvSections.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

    const fileName = generateCompleteFileName({
      vertical: vertical,
      exportType: 'specific-activity',
      specificActivityType: `Registry`,
      tin: verticalEntity?.entityDetails?.tin,
      onlineName: verticalEntity?.entityDetails?.name,
      entityId: verticalEntity?.id,
    })
    const finalFileName = `${fileName}.csv`

    saveAs(blob, finalFileName)
  }, [
    rows,
    verticalEntity?.overviewDetails?.businessNames,
    verticalEntity?.overviewDetails.locations,
    verticalEntity?.entityDetails?.tin,
    verticalEntity?.entityDetails?.name,
    verticalEntity?.id,
    vertical,
  ])
}
